import {
  FunctionComponent,
  useRef,
  useCallback,
  useEffect,
  useState,
} from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useHistory, useParams, useLocation } from 'react-router'
import { Location } from 'history'
import useStyles from '@/modules/Auth/pages/Login/styles'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import services from '../../auth-services'
import { IUser } from '@/modules/Auth/auth-types'
import { getThemeByUniqueCode } from '@/theme'
import { getThemeByUniqueCode as getRebrandedThemeByUniqueCode } from '@/rebrandedTheme'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import StartForm from '@/modules/Auth/components/StartForm'
import { initializeBrowserNotification } from '@/components/BrowserNotification'
import { setCache, getCache } from '@/utils/cache'
import AuthLayout from '@/components/AuthLayout'
import { RenderIf } from '@/components/RenderIf'
import { handleLoginError } from './utils'
import { Text, Button, Input, ConexaLogo } from '@conexasaude/conexa-hero'
import { HeroWrapper } from '@/styles/rebranded/HeroWrapper'
import { useForm } from 'react-hook-form'
import { useRebrandingThemeGeneratorContext } from '@/rebrandedTheme/contexts/ThemeGeneratorContext'
import { openWhatsappSupport } from '@/utils/openWhatsappSupport'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'

type LoginLocation = {
  from: Location
}

const Login: FunctionComponent = () => {
  const params = useParams<{ clinic?: string }>()
  const location = useLocation<LoginLocation>()
  const { push } = useHistory()
  const [loading, setLoading] = useState(false)
  const [inputError, setInputError] = useState(false)
  const [form, setForm] = useState('start-form')
  const [imageError, setImageError] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const { theme, setThemeColors } = useThemeGeneratorContext()
  const { theme: rebrandedTheme, setThemeColors: setRebrandedThemeColors } =
    useRebrandingThemeGeneratorContext()
  const { signIn } = useAuthContext()
  // const { showModal } = useModalContext()
  const styles = useStyles()
  const { handleSubmit, register } = useForm()
  const { logAnalyticsEvent } = useFirebaseContext()

  const redirectIfAuthenticated = (pathname?: string) => {
    if (location?.state?.from) {
      push(location?.state?.from?.pathname)
      return
    }

    push(pathname)
  }

  useEffect(() => {
    if (getCache('user')) {
      redirectIfAuthenticated('/')
      return
    }

    if (params.clinic) {
      getThemeByUniqueCode(params.clinic, theme)
        .then(res => setThemeColors(res))
        .catch(() => setThemeColors(null))

      getRebrandedThemeByUniqueCode(params.clinic, rebrandedTheme)
        .then(res => setRebrandedThemeColors(res))
        .catch(() => setRebrandedThemeColors(null))

      setCache('unique-code', params.clinic)
    } else {
      setThemeColors(null)
      setRebrandedThemeColors(null)
      setCache('unique-code', '')
    }

    // window?.Appcues?.anonymous()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(
    async ({ user, pass }) => {
      try {
        setInputError(false)
        setLoading(true)
        const token = await recaptchaRef.current.executeAsync()
        const users: IUser[] = await services.login(user, pass, token, true)

        signIn(users)
        setLoading(false)
        logAnalyticsEvent('Pac_Login_Entrar', { response_status: 'ok' })
        recaptchaRef.current.reset()
        initializeBrowserNotification()
        if (params.clinic)
          push(`/escolha/${params.clinic}`, {
            from: location?.state?.from,
          })
        else
          push(`/escolha`, {
            from: location?.state?.from,
          })
      } catch (e) {
        recaptchaRef.current.reset()
        setInputError(true)
        setLoading(false)
        logAnalyticsEvent('Pac_Login_Entrar', { response_status: 'error' })

        handleLoginError(e, redirectToActivation)
      }
    },
    // eslint-disable-next-line
    [params.clinic, push, signIn, logAnalyticsEvent],
  )

  const goToForgotPassword = useCallback(() => {
    logAnalyticsEvent('Pac_Login_EsqueciSenha')

    if (params.clinic) {
      push(`/esqueci-senha/${params.clinic}`)
      return
    }

    push('/esqueci-senha')
  }, [params.clinic, push, logAnalyticsEvent])

  const goToCreateAccount = useCallback(() => {
    logAnalyticsEvent('Pac_Login_CriarConta')
    push('/cadastro/inicio')
  }, [push, logAnalyticsEvent])

  const redirectToActivation = useCallback(() => {
    let activationUrl = process.env.REACT_APP_ACTIVATION_PAGE_URL + '/passo1'
    if (params?.clinic) {
      activationUrl += `/${params?.clinic}`
    }
    window.open(activationUrl, '_self')
  }, [params?.clinic])

  return (
    <AuthLayout>
      <HeroWrapper>
        <div css={styles.container}>
          {form && form === 'start-form' ? (
            <StartForm onClickLogin={() => setForm('login-form')} />
          ) : (
            <div css={styles.loginForm}>
              <RenderIf condition={!!params.clinic && !imageError}>
                <img
                  src={`${process.env.REACT_APP_LINK_LOGO_AWS}${params.clinic}.png`}
                  alt="logo"
                  css={styles.logoWhiteLabel}
                  onError={event => {
                    event.currentTarget.onerror = null
                    setImageError(true)
                    event.currentTarget.style.height = 'auto'
                  }}
                />
              </RenderIf>
              <RenderIf condition={!params.clinic || imageError}>
                <ConexaLogo base="colorful" inverse width={270} height={60} />
              </RenderIf>

              <Text
                variant="headingMedium"
                css={styles.subtitleLoginInstructions}
              >
                Utilize o seu e-mail ou CPF e senha cadastrada para entrar.
              </Text>

              <form autoComplete="off" onSubmit={handleSubmit(login)}>
                <div css={styles.containerFirstInput}>
                  <Input
                    label="E-mail ou CPF"
                    placeholder="Digite seu e-mail ou número do seu CPF"
                    {...(inputError && {
                      error: 'Verifique seu e-mail ou CPF',
                    })}
                    {...register('user')}
                    fullWidth
                  />
                </div>
                <Input
                  label="Senha"
                  name="pass"
                  type="password"
                  placeholder="Digite a sua senha"
                  {...(inputError && {
                    error: 'Verifique sua senha',
                  })}
                  {...register('pass')}
                  fullWidth
                />
                <br />

                <Button
                  busy={loading}
                  fullWidth
                  id="Login-AcessarConta-CTA_Entrar"
                >
                  Entrar
                </Button>

                <div css={styles.linkLogin}>
                  <Text
                    renderAs="span"
                    variant="linkUnderline"
                    onClick={goToForgotPassword}
                  >
                    Esqueceu sua senha?
                  </Text>
                  <span css={styles.divider}>|</span>
                  <Text
                    renderAs="span"
                    variant="linkUnderline"
                    onClick={goToCreateAccount}
                  >
                    Crie sua conta
                  </Text>
                </div>
                <Text
                  renderAs="span"
                  variant="linkUnderline"
                  onClick={openWhatsappSupport}
                  css={styles.linkSupport}
                >
                  Precisa de ajuda?
                </Text>

                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                />
              </form>
            </div>
          )}
        </div>
      </HeroWrapper>
    </AuthLayout>
  )
}

export default Login
